<script>
  import MainPageContent from '#lib/MainPageContent.svelte';
  import t from '#lib/i18n.svelte.js';
  import BlockGrid from '#lib/BlockGrid.svelte';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import TextInput from '#lib/TextInput.svelte';
  import Text from '#lib/Text.svelte';
  import Popover from '#lib/Popover.svelte';
  import Icon from '#lib/Icon.svelte';
  import ScrollIndicator from '#lib/ScrollIndicator.svelte';
  import TagSelector from '#lib/TagSelector.svelte';
  import Tag from '#lib/Tag.svelte';
  import { enhance } from '$app/forms';
  import { page } from '$app/state';
  import { refreshAll,  } from '$app/navigation';
  import { getContext } from 'svelte';
  import { browser } from '$app/env';
  import Avatar from '#lib/Avatar.svelte';
  import SegmentedControl from '#lib/SegmentedControl.svelte';
  import Footer from '#lib/Footer.svelte';
  import UserDetailsDialog from '#lib/UserDetailsDialog.svelte';

  import {
    format_date,
    is_same_day, 
    add_days,
    fetch_json,
    render_safe_micro_markdown,
    get_user_slug
  } from '#lib/util.js';
  
  let site_settings = getContext('site_settings');

  // Get the browser's timezone
  const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
  let { 
    conversations, 
    messages, 
    conversation_id, 
  } = $props();

  let message_input_value = $state();
  let message_input_ref = $state();
  // One tag menu and one settings menu for the whole list, anchored to the row that opened them:
  // a Popover (and a tag fetch) per row made "show all contacts" render hundreds of them
  let tag_menu = $state({ ref: undefined, anchor: undefined, conversation: undefined });
  let settings_menu = $state({ ref: undefined, anchor: undefined, conversation: undefined });
  let search_query = $state('');
  let current_conversation = $derived(conversations?.find(conversation => conversation.conversation_id === conversation_id));
  let messages_grouped_by_day = $derived(group_messages_by_day(messages));
  let previous_message_count = $state(0);
  let show_all_contacts = $state(false);
  /** @type {'all'|'unread'|'awaiting_reply'} */
  let conversation_filter = $state('all');
  let conversations_with_messages = $derived(conversations.filter(conversation => conversation.last_message));
  let filter_options = $derived([
    { value: 'all', title: `${t('messages_filter_all')} ${conversations_with_messages.length}` },
    { value: 'unread', title: `${t('messages_filter_unread')} ${conversations_with_messages.filter(c => !c.is_read_by_admin).length}` },
    { value: 'awaiting_reply', title: `${t('messages_filter_awaiting_reply')} ${conversations_with_messages.filter(c => c.awaiting_reply_from_admin).length}` }
  ]);
  let filtered_conversations = $derived(search_conversations(search_query));

  // Hundreds of rows at once took over a second to render and made scrolling heavy, so the
  // list grows in batches as its end scrolls into view. A new search or filter starts over.
  const CONVERSATIONS_BATCH = 50;
  let rendered_count = $state(CONVERSATIONS_BATCH);
  let rendered_conversations = $derived(filtered_conversations.slice(0, rendered_count));
  $effect.pre(() => {
    filtered_conversations;
    rendered_count = CONVERSATIONS_BATCH;
  });

  /** Renders the next batch while the element at the end of the list is near the viewport */
  function render_more_when_visible(node) {
    const observer = new IntersectionObserver((entries) => {
      if (entries.some(entry => entry.isIntersecting)) rendered_count += CONVERSATIONS_BATCH;
    // The list scrolls inside its own container; with the viewport as root the margin would not reach into it
    }, { root: node.closest('.scroll-indicator'), rootMargin: '600px 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }
  let user_details_dialog_user = $state();

  function group_messages_by_day(messages) {
    return messages.reduce((acc, message) => {
      const date = format_date(message.created_at, 'YDM', undefined, BROWSER_TIMEZONE);
      if (acc.length === 0 || acc[acc.length - 1].date !== date) {
        acc.push({ date, messages: [] });
      }
      acc[acc.length - 1].messages.push(message);
      return acc;
    }, []);
  }

  function search_conversations(search_query) {
    const query = search_query.toLowerCase().trim();

    // Unread and awaiting reply narrow the conversations that have messages; a search narrows further
    if (conversation_filter !== 'all') {
      return conversations_with_messages.filter(conversation => {
        if (conversation_filter === 'unread' && conversation.is_read_by_admin) return false;
        if (conversation_filter === 'awaiting_reply' && !conversation.awaiting_reply_from_admin) return false;
        return !query || matches_query(conversation, query);
      });
    }
    
    if (show_all_contacts || query) {
      return conversations.filter(conversation => {
        if (show_all_contacts && !query) return true;
        return matches_query(conversation, query);
      });
    }
    
    return conversations_with_messages;
  }

  function matches_query(conversation, query) {
    if (conversation.user_name.toLowerCase().includes(query)) return true;
    if (conversation.user_email.toLowerCase().includes(query)) return true;
    if (conversation.last_message?.toLowerCase().includes(query)) return true;
    if (conversation.tag_key?.toLowerCase().includes(query)) return true;
    return false;
  }

  function toggle_show_all_contacts() {
    show_all_contacts = !show_all_contacts;
    search_query = '';
  }

  // The row's conversation, not the one open on the right
  async function delete_conversation(conversation) {
    settings_menu.ref?.hidePopover();
    if (!page.data.current_user || page.data.current_user.role !== 'admin') {
      alert('Sorry. You must sign in first.');
      return;
    }
    if (confirm('Are you sure you want to delete this conversation? It cannot be undone.')) {
      try {
        const result = await fetch_json('POST', '/api/delete-conversation', conversation.conversation_id);
        if (result.success) {
          alert('Conversation deleted');
          refreshAll();
        }
      } catch (err) {
        console.error(err);
      }
    }
  }

  $effect(() => {
    const interval = setInterval(() => {
      if (document.hasFocus() && page.data.current_user) {
        refreshAll();
      }
    }, 10000);

    return () => {
      clearInterval(interval);
    };
  });

  $effect(() => {
    if (message_input_ref) {
      message_input_ref.focus();
    }
  });

  $effect(() => {
    // prevent scrolling to bottom while reading the messages due to interval
    if (conversation_id && messages.length > previous_message_count) {
      scroll_to_bottom_of_messages();
      previous_message_count = messages.length;
    }
  });

  $effect(() => {
    // Check for a 'text' parameter in the URL, and if present, 
    // sets the message input value to the decoded text
    if (browser) {
      const urlParams = new URLSearchParams(window.location.search);
      const textParam = urlParams.get('text');
      
      if (textParam && page.data.current_user?.user_id) {
        message_input_value = decodeURIComponent(textParam);
        
        // Remove the 'text' parameter from the URL
        urlParams.delete('text');
        const newUrl = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;
        history.replaceState(null, '', newUrl);
      }
    }
  });

  function scroll_to_bottom_of_messages() {
    const conversation_messages = document.querySelector('.conversation-messages');
    if (conversation_messages) {
      conversation_messages.scrollTo({
        top: conversation_messages.scrollHeight,
        behavior: 'smooth'
      });
    }
  }

</script>

<svelte:head>
  <title>{t('messages')}</title>
</svelte:head>

<MainPageContent>
  <div class='messenger'>
    <BlockGrid>
      {#if page.data.current_user?.role === 'admin'}
        <div
          class='relative conversations-container full-width'
          class:collapsed={Boolean(page.url.searchParams.get('conversation_id'))}
        >
            <div class='search-container sticky px py p-4'>
              <div class='border-1 border-sm flex-row selected-outline-canvas-if-has-child-in-focus'>
                <label for='search-conversations' class='items-center flex-row'>
                  <ButtonLabel start_icon='search' variant='brand ghostly' />
                </label>
                <TextInput
                  id='search-conversations'
                  placeholder={t('search_contacts')}
                  bind:value={search_query}
                  variant='canvas ghostly'
                />
              </div>
              <SegmentedControl
                class='conversation-filter mbs m-3'
                options={filter_options}
                bind:value={conversation_filter}
              />
            </div>

            <ul class='conversations-list auto-scroll-y'>
              {#if conversations?.length > 0}
                <ScrollIndicator>
                  {#each rendered_conversations as conversation (conversation.conversation_id)}
                    <div 
                      class='conversation-preview selected-outline-canvas-on-focus'
                      class:active={conversation.conversation_id === conversation_id}
                    >
                    
                      <a 
                        class='conversation-link gap-y g-4'
                        href={`?conversation_id=${conversation.conversation_id}`}
                        draggable="false"
                        onclick={(e) => {
                          // Prevent navigation only if there's a meaningful text selection within this link
                          // To allow users to select text within the conversation link
                          const selection = window.getSelection();
                          if (selection && selection.toString().trim().length > 0) {
                            // Check if the selection is within this conversation link
                            const range = selection.getRangeAt(0);
                            if (e.currentTarget.contains(range.commonAncestorContainer)) {
                              e.preventDefault();
                              return;
                            }
                          }
                        }}
                      >
                        <Avatar 
                          class='conversation-avatar border-1' 
                          name={conversation?.user_name} 
                          avatar={conversation?.user_avatar} 
                        />
                        <div class='conversation-name flex-row gap-x g-2'>
                          <div class='flex-column gap-y g-4'>
                            <h3 class='text-control leading-trim'>{conversation?.user_name}</h3>
                            <p class='conversation-email text-control text-3 truncate leading-trim size-reduce-1'>{conversation?.user_email}</p>
                          </div>
                        </div>
                        <!-- conversation.last_message_at is 1970 if the conversation was deleted -->
                        {#if conversation.last_message}
                          <div class='conversation-status flex-row items-center'>
                            {#if !conversation.is_read_by_admin}
                              <Icon name='disc' style='--icon-color1: var(--editing);' />
                            {:else if !conversation.awaiting_reply_from_admin}
                              <Icon name='enter' style='--icon-color1: var(--color-default-foreground-500);' />
                            {/if}
                          </div>
                          <div class='conversation-message flex-row gap-x g-3 items-center'>
                            <!-- Tag display for admin users -->
                            {#if page.data.current_user?.role === 'admin' && conversation.tag_key}
                              {#key conversation.tag_key}
                                <Tag>{t(conversation.tag_key)}</Tag>
                              {/key}
                            {/if}
                            <p class='text-control truncate leading-trim size-reduce-1'>{conversation.last_message}</p>
                          </div>
                        {/if}
                      </a>

                      {#if conversation.last_message}
                        <p class='conversation-date text-control text-3 size-reduce-1 leading-trim'>{format_date(conversation.last_message_at, 'YDMHm', undefined, BROWSER_TIMEZONE)}</p>
                      {/if}

                      <!-- Only show if the conversation has messages -->
                      {#if conversation.last_message}
                        <!-- Tag selector for admin users -->
                        <button
                          class='conversation-tag justify-self-end px py p-4'
                          popovertarget='conversation_tag_menu_popover'
                          title={t('add_tag')}
                          onclick={(e) => { tag_menu.anchor = e.currentTarget; tag_menu.conversation = conversation; }}
                        >
                          <Icon name='tag' />
                        </button>
                      {/if}

                      <!-- Delete conversation for admin users -->
                      <button
                        class='conversation-ellipsis justify-self-end px py p-4'
                        popovertarget='conversation_settings_menu_popover'
                        title={t('settings')}
                        onclick={(e) => { settings_menu.anchor = e.currentTarget; settings_menu.conversation = conversation; }}
                      >
                        <Icon name='dot-dot-dot' />
                      </button>
                    </div>
                  {/each}

                  {#if filtered_conversations.length === 0}
                    <p class='text-control text-3 px py p-4'>{t('no_matching_conversations')}</p>
                  {/if}

                  {#if rendered_count < filtered_conversations.length}
                    <!-- Re-attached per batch, so a sentinel still in view after rendering loads the next one -->
                    {#key rendered_count}
                      <div class='render-more-sentinel' aria-hidden='true' {@attach render_more_when_visible}></div>
                    {/key}
                  {/if}
                  
                  {#if !show_all_contacts && !search_query && conversation_filter === 'all'}
                    <div class='show-all-contacts-container flex-row justify-center py p-4'>
                      <button 
                        onclick={toggle_show_all_contacts}
                        class='show-all-contacts-button'
                      >
                        <ButtonLabel 
                          variant='brand ghostly' 
                          start_icon='users'
                        >
                          {t('show_all_contacts')}
                        </ButtonLabel>
                      </button>
                    </div>
                  {/if}
                </ScrollIndicator>
              {:else if search_query}
                <div class='flex-column items-center gap-y g-4 py p-8'>
                  <p class='text-control'>{t('no_matching_conversations')}</p>
                  <button 
                    onclick={toggle_show_all_contacts}
                    class='show-all-contacts-button'
                  >
                    <ButtonLabel 
                      variant='brand ghostly' 
                      start_icon='users'
                    >
                      {t('show_all_contacts')}
                    </ButtonLabel>
                  </button>
                </div>
              {:else}
                <p class='text-control'>{t('no_conversations')}</p>
              {/if}
            </ul>

            <Popover
              bind:ref={tag_menu.ref}
              anchor={tag_menu.anchor}
              id='conversation_tag_menu_popover'
              placement='bottom-end'
              offset={-20}
            >
              {#if tag_menu.conversation}
                {#key tag_menu.conversation.conversation_id}
                  <TagSelector
                    conversation_id={tag_menu.conversation.conversation_id}
                    current_tag_key={tag_menu.conversation.tag_key}
                    on_tag_change={() => {
                      tag_menu.ref?.hidePopover();
                      refreshAll();
                    }}
                  />
                {/key}
              {/if}
            </Popover>

            <Popover
              bind:ref={settings_menu.ref}
              anchor={settings_menu.anchor}
              id='conversation_settings_menu_popover'
              placement='bottom-end'
              offset={-20}
            >
              {#if settings_menu.conversation}
                {@const conversation = settings_menu.conversation}
                <div class='flex-column'>
                  <button onclick={() => { user_details_dialog_user = { user_id: conversation.conversation_id, name: conversation.user_name, email: conversation.user_email, avatar: conversation.user_avatar }; settings_menu.ref?.hidePopover(); }}>
                    <ButtonLabel start_icon='calm-face' align='start'>
                      {t('user_details')}
                    </ButtonLabel>
                  </button>
                  <button onclick={() => delete_conversation(conversation)}>
                    <ButtonLabel start_icon='trash' align='start' variant='system-destructive ghostly'>
                      {t('delete_conversation')}
                    </ButtonLabel>
                  </button>
                </div>
              {/if}
            </Popover>
        </div>
      {/if}
      <div class='conversation-detail' class:hidden-on-mobile={!page.url.searchParams.get('conversation_id')}>

        {#if page.data.current_user?.role === 'admin' && conversations.length > 0 && page.url.searchParams.get('conversation_id')}
          <div class='back-to-message-list shadow-sm flex-row items-center gap-x g-2 bg full-width px justify-between'>
            <a href="/messages" title={t('messages')}>
              <ButtonLabel variant='brand flush' start_icon={'arrow-left'}>
                {t('messages')}
              </ButtonLabel>
            </a>
            <ButtonLabel variant='brand ghostly' align='end' class='bg'>{current_conversation.user_name}</ButtonLabel>
          </div>
        {/if}


        {#if page.data.current_user?.role === 'admin' && (!conversations || conversations.length === 0)}
          <div class="no-conversations-notice flex-column items-center justify-center my m-10">
            <Icon name='calm-face' style='--icon-size: 5vh; --icon-color1: var(--color-default-foreground-500);' />
            <p class="text-control text-center">{t('waiting_for_user_messages')}</p>
          </div>
        {:else}
          

          <div class='conversation-messages flex-column px'>

            {#if page.data.current_user?.role !== 'admin'}
              <div class='my m-8'>
                <h1 class='text-heading1'>{site_settings.messages_title || t('send_us_a_message')}</h1>
                <Text 
                  content={site_settings.messages_intro || t('use_our_messenger_to_contact_us')}
                  variant='body'
                  class='py p-4'
                />
              </div>
            {/if}

            {#if !page.data.current_user}
              <a class='sign-in-to-send-message' href="/login?return_to={encodeURIComponent(`/messages${page.url.search}`)}">
                <ButtonLabel variant='brand {site_settings.primary_control_surface}'>
                  {t('sign_in_and_send_message')}
                </ButtonLabel>
              </a>
            {/if}

            {#if messages.length === 0}
              <div class='conversation-messages-placeholder'>
                <Icon name='messenger-illustration' style='--icon-size: min(max(30vh, 300px), 90vw);' />
              </div>
            {/if}

            {#each messages_grouped_by_day as { date, messages: messages_for_day }, group_index (date)}
              <div class='messages-group'>
                {#if group_index !== 0}
                  <hr class='my m-8'>
                {/if}
                <div class='day-separator text-control size-reduce-1'>
                  {#if is_same_day(new Date(messages_for_day[0].created_at), new Date())}
                    {t('today')}
                  {:else if is_same_day(new Date(messages_for_day[0].created_at), add_days(new Date(), -1))}
                    {t('yesterday')}
                  {:else}
                    {format_date(messages_for_day[0].created_at, 'YDM', undefined, BROWSER_TIMEZONE)}
                  {/if}
                </div>
                {#each messages_for_day as message, msg_index (message.message_id)}
                  <div 
                    class='message flex-row{message.name === page.data.current_user.name ? "-reverse" : ""} items-end gap-x g-3'
                    class:message-by-current-user={message.name === page.data.current_user.name}
                    class:message-by-other-user={message.name !== page.data.current_user.name}
                    class:message-by-admin={message.name !== current_conversation?.user_name}
                  >
                    <button 
                      type='button'
                      class='message-avatar flex'
                      onclick={() => { user_details_dialog_user = { user_id: message.user_id, name: message.name, email: message.email, avatar: message.user_avatar }; }}
                      title={t('open')}
                    >
                      <Avatar name={message.name} avatar={message.user_avatar} />
                    </button>
                    <div class='message-content'>
                      <div class='message-text text-body size-reduce-1'>{@html render_safe_micro_markdown(message.message)}</div>

                      <div class='message-footer flex-row gap-x g-5 items-center'>

                        <!-- Message Sent Date -->
                        <div class='message-date text-3 text-control leading-trim size-reduce-1'>                    
                          {format_date(message.created_at, 'Hm', undefined, BROWSER_TIMEZONE)}
                        </div>

                        <!-- Read status for admin messages -->
                        {#if message.name === page.data.current_user.name}
                          <div class='message-read-indicator flex-row gap-x g-2 items-center'>
                            {#if current_conversation?.user_last_read_at && new Date(message.created_at) <= new Date(current_conversation.user_last_read_at)}
                              <Icon name='double-check' style='--icon-color1: var(--color-default-foreground-500);' />
                              <span class='text-3 text-control leading-trim size-reduce-1'>{t('message_read')}</span>
                            {:else}
                              <Icon name='check' style='--icon-color1: var(--color-default-foreground-500);' />
                              <span class='text-3 text-control leading-trim size-reduce-1'>{t('message_sent')}</span>
                            {/if}
                          </div>
                        {/if}

                        <!-- Read status for user messages -->
                        {#if message.name !== page.data.current_user.name}
                          <div class='message-read-indicator flex-row gap-x g-2 items-center'>
                            {#if current_conversation?.admin_last_read_at && new Date(message.created_at) <= new Date(current_conversation.admin_last_read_at)}
                              <Icon name='double-check' style='--icon-color1: var(--color-default-foreground-500);' />
                              <span class='text-3 text-control leading-trim size-reduce-1'>{t('message_read')}</span>
                            {:else}
                              <Icon name='check' style='--icon-color1: var(--color-default-foreground-500);' />
                              <span class='text-3 text-control leading-trim size-reduce-1'>{t('message_sent')}</span>
                            {/if}
                          </div>
                        {/if}
                      </div>
                    </div>
                  </div>
                {/each}
              </div>
            {/each}
            
            {#if current_conversation?.read_by_user}
              <p class='text-control size-reduce-1 text-3 self-center'>{t('read_by_user')} {format_date(current_conversation.last_read_at_by_user, 'YDMHm')}</p>
            {/if}
          </div>
        {/if}
        
        
        <!-- Reply box -->
        {#if page.data.current_user}
          <form 
            class='conversation-reply'
            method='POST' 
            action="?/send_message" 
            use:enhance={() => {
              return async ({ result, update }) => {
                if (result.type === 'success') {
                  await update();
                  message_input_value = '';
                  // Use setTimeout to ensure DOM has updated before scrolling
                  setTimeout(scroll_to_bottom_of_messages, 0);
                  setTimeout(() => {
                    document.getElementById('message').focus();
                  }, 100);
                } else {
                  await update();
                }
              };
            }}
          >
            <input type='hidden' name='message' value={message_input_value?.trim() || ''} />
            <TextInput 
              bind:value={message_input_value}
              bind:ref={message_input_ref}
              disabled={!page.data.current_user || (page.data.current_user.role === 'admin' && (!conversations || conversations.length === 0 || !conversation_id))}
              id='message' 
              variant='brand subtle-outlined'
              text_style='text-body size-reduce-1'
              class='px py p-4 bg-none no-max-width'
              multiline={true}
              placeholder={messages.length === 0 ? t('empty_message_prompt', { user_name: page.data.current_user.name }) : t('response_input_placeholder')}
              onkeydown={(e) => {
                // trigger form submission
                if (e.key === 'Enter' && e.metaKey) {
                  e.preventDefault();
                  if(message_input_value?.trim()) {
                    document.getElementById('submit-message').click();
                  }
                }
              }}
            />
            {#if page.data.current_user?.role === 'admin'}
              <input type='hidden' name='conversation_id' value={conversation_id} />
            {/if}
            <button 
              id='submit-message' 
              type='submit' 
              disabled={!page.data.current_user || !message_input_value?.trim()} 
              title={t('send')}
            >
              <ButtonLabel 
                start_icon='paperplane'
                text_style='text-control' 
                variant='brand {site_settings.primary_control_surface}' />
            </button>
          </form>
        {/if}

      </div>

    </BlockGrid>
  </div>

  <!--
    Footer for signed-out visitors only.

    Signed in, /messages is an app view — a chat you are working in — and a site footer full of
    navigation is noise below it. Signed out it is effectively a contact page reached from the
    public site, so it needs the same way back into the rest of the site as any other page.

    That is not only cosmetic: Impressum and Datenschutz live in the footer, and German law
    requires them to be reachable from every public page. Rendering no footer at all left a
    signed-out visitor on this route with no route to either.

    The condition sits INSIDE the snippet, not around it: a snippet only becomes a component prop
    when it is a direct child of that component, so wrapping this in `{#if}` would scope it to the
    block and MainPageContent would never receive a footer at all. Declaring it unconditionally
    and rendering nothing inside costs nothing — the footer grid row is `auto`.
  -->
  {#snippet footer()}
    {#if !page.data.current_user}
      <Footer />
    {/if}
  {/snippet}
</MainPageContent>

{#if user_details_dialog_user}
  <UserDetailsDialog 
    user={user_details_dialog_user}
    onclose={() => { user_details_dialog_user = undefined; }}
  />
{/if}

<style lang='postcss'>

  .messenger {
    height: 100%;
    :global(> *) {
      height: 100%;
    }
    --message-radius: max(var(--radius-sm), var(--size-3));
  }

  .back-to-message-list {
    z-index: 4;
    padding-block: var(--size-3);
    left: 0;
    position: fixed;
    top: var(--dynamic-island-bottom-edge)
  }
  @media (max-width: 1024px) {
    .conversation-detail {
      padding-block-start: 100px;
    }
  }
  @media (min-width: 1025px) {
    .back-to-message-list {
      display: none;
    }
  }

  .messenger:has(.conversations-container) {
    .conversation-detail.hidden-on-mobile { 
      @media (max-width: 640px) {
        display: none;
      }
    }    
    .conversation-detail {
      height: calc(100dvh - var(--dynamic-island-bottom-edge));
      grid-column: var(--dynamic-island-track-start) / var(--dynamic-island-track-end);
      @media (min-width: 1025px) {
        grid-column: 23 / -1;
        display: grid;
        grid-template-columns: subgrid;
        .conversation-messages,
        .conversation-reply {
          grid-column: 1 / -1;
          padding-inline-end: var(--page-inline-spacing);
        }
        .conversation-messages {
          padding-block-end: 100px;
        }
      }
    }
  }

  .conversations-container, .conversation-detail {
    overflow-y: auto;
    max-height: 100%;
  }
  .conversations-container {
    display: grid;
    grid-template-rows: auto 1fr;
    grid-row: 1 / 2;
    max-height: calc(100dvh - var(--dynamic-island-bottom-edge));
    grid-column: var(--dynamic-island-track-start) / 22;
    @media (max-width: 1024px) {
      grid-column: var(--dynamic-island-track-start) / 26;
    }
    @media (max-width: 640px) {
      grid-column: var(--dynamic-island-track-start) / var(--dynamic-island-track-end);
    }
    z-index: 2;
    transition: transform 300ms cubic-bezier(.4,0,.6,1);
    @media (min-width: 641px) {
      /* padding-inline-end: var(--grid-center-gutter-left); */
      border-inline-end: var(--color-default-stroke) 1px solid;
    }
    background-color: var(--color-default-background);
    /* padding-inline: max(var(--grid-gutter), var(--page-inline-spacing)); */
    &.collapsed {
      @media (max-width: 1024px) {
        transform: translateX(-120%);
      }
    }
    .search-container {
      z-index: 1;
    }
    .conversation-preview {
      /* padding-inline: var(--size-3); */
      display: grid;
      grid-template-columns: 1fr 48px 48px;
      border-radius: 0;
      width: 100%;
      min-width: 0;
      min-height: 80px;
      transition: background-color 40ms ease-in-out;
      /* improve scroll performance */
      /* contain-intrinsic-height: 80px;
      content-visibility: auto; */
      grid-template-areas: 
        "link tag ellipsis"
        "link tag ellipsis"
        "link tag ellipsis";

      @media (hover: hover) {
        &:has(.conversation-link:hover) {
          background-color: var(--color-default-background-25);
        }
        .conversation-tag, .conversation-ellipsis {
          opacity: 0;
          transition: opacity 80ms ease-in-out
        }
        &:hover, &.active {
          background-color: var(--color-default-background-50);
          .conversation-tag, .conversation-ellipsis {
            opacity: 1;
          }
        }
        &:focus-within {
          .conversation-tag, .conversation-ellipsis {
            opacity: 1;
          }
        }
      }
      .conversation-link {
        padding: var(--size-4);
        user-select: none;
        grid-area: link;
        z-index: 1;
        display: grid;
        column-gap: var(--size-3);
        grid-template-columns: var(--avatar-size) auto;
        grid-template-rows: auto auto;
        grid-template-areas: 
          "conversation-avatar conversation-name"
          "status conversation-message";
        > * {
          min-width: 0;
        }
        :global(.conversation-avatar) {
          grid-area: conversation-avatar;
          :global(circle) {
            fill: var(--color-default-background-50);
          }
        }
        .conversation-message *, .conversation-email {
          letter-spacing: 0;
          text-transform: none;
          --size-modifier: 1;
        }
        .conversation-email {
          user-select: text;
        }
        .conversation-name {
          grid-area: conversation-name;
          user-select: text;
        }
        .conversation-message {
          grid-area: conversation-message;
        }
        .conversation-status {
          grid-area: status;
          display: flex;
          justify-content: center;
        }
        &:not(:has(.conversation-message)) {
          align-self: center;
        }
      }
      .conversation-date {
        grid-area: ellipsis;
        justify-self: end;
        white-space: nowrap;
        padding-block: var(--size-4);
        padding-inline-end: var(--size-7);
      }
      .conversation-ellipsis,
      .conversation-tag {
        z-index: 1;
        height: 100%;
        &:hover {
          opacity: 0.6;
        }
      }
      .conversation-tag {
        grid-area: tag;
      }
      .conversation-ellipsis {
        grid-area: ellipsis;
      }
      .conversation-link,
      .conversation-date,
      .conversation-ellipsis {
        min-width: 0;
      }
    }
  }

  .conversation-detail {
    display: grid;
    grid-row: 1 / 2;
    grid-column: 1 / -1;
    grid-template-columns: inherit;
    /* padding-block-start: var(--size-4); */
    @media (min-width: 1025px) {
      height: fit-content;
    }
    &:has(.messages-group) {
      height: calc(100dvh - var(--dynamic-island-bottom-edge));
    }
    .conversation-messages {
      padding-block-end: var(--size-8);
      flex-grow: 1;
      grid-column: 10 / 42;

      .messages-group {
        position: relative;
        .day-separator {
          margin-block-end: var(--size-6);
          position: sticky;
          top: var(--size-4);
          margin-inline: auto;
          width: fit-content;
          z-index: 1;
          background-color: var(--color-default-foreground-500);
          backdrop-filter: blur(10px);
          color: var(--color-default-background);
          padding: var(--size-3) var(--size-5);
          border-radius: var(--round);
        }
      }

      .message {
        margin-block-end: var(--size-4);
        .message-content {
          display: grid;
          grid-template-columns: 1fr auto;
          grid-template-rows: 1fr auto;
          row-gap: var(--size-3);
          position: relative;
          width: fit-content;
          padding: var(--message-radius);
          border: 1px solid var(--color-default-stroke);
          border-radius: var(--message-radius);
          max-width: min(max(min(80%, 60ch), 40ch), 100%);
          grid-template-areas: 
            "text text"
            ". name-and-date";
        }
        & + .message-by-current-user {
          border-top-left-radius: 0;
          border-top-right-radius: 0;
        }
        &.message-by-current-user .message-content {
          margin-left: auto; 
        }
        &.message-by-other-user .message-content {          
          background-color: var(--color-default-background-25);
          grid-template-columns: auto 1fr;
          grid-template-areas: 
          "text text"
          "name-and-date .";
        }
        .message-text {
          grid-area: text;
          width: fit-content;
        }        
        .message-footer {
          grid-area: name-and-date;
        }
        .message-avatar {
          /* Align avatar with message footer */
          margin-block-end: calc(
            var(--message-radius)
            - 0.25 * var(--avatar-size)
          );
          :global(circle) {
            fill: var(--color-default-background-50);
          }
        }
        
        /* Hide avatar for consecutive messages by current user */
        &.message-by-current-user:has(+ .message.message-by-current-user) .message-avatar {
          visibility: hidden;
        }
      }
      
    }
    .conversation-reply {
      grid-column: 10 / 42;
      position: sticky;
      bottom: 0;
      right: 0;
      width: 100%;
      background-color: var(--color-default-background);
      padding-block-start: var(--size-2);
      padding-block-end: var(--page-inline-spacing);
      z-index: 1;
      box-shadow: 0 -10px 20px var(--color-default-background), 0 -10px 40px var(--color-default-background);
      display: grid;
      grid-template-columns: 1fr;
      align-items: end;
      justify-items: end;
      :global(*) {
        grid-column: 1 / 2;
        grid-row: 1 / 2;
      }
      :global(#submit-message) {
        z-index: 1;
        margin-block-end: var(--size-3);
        margin-inline-end: var(--size-3);
      }
      :global(#submit-message .button-label),
      :global(#message) {
        border-radius: calc((var(--touchable-target-min-size) + var(--size-3)) / 2);
      }
      :global(#message) {
        padding: calc(var(--touchable-target-min-size) / 2);
        padding-block-end: 3lh;
      }
    }
  }
  .no-conversations-notice {
    grid-row: 1 / 3;
    grid-column: 1 / calc(var(--dynamic-island-track-end) - 23 + 1);
  }
  .conversation-messages-placeholder {
    max-height: max(30vh, 300px);
    border: none;
    margin: auto;
  }
  .sign-in-to-send-message {
    width: 100%;
  }
  .messenger :global(#message) {
    min-height: calc(2 * var(--size-3) + 4lh);
    padding-block: var(--size-3);
    max-height: 40vh;
  }
  .back-to-message-list,
  .conversation-messages,
  .conversation-reply {
    @media (max-width: 1024px) {
      padding-inline: var(--page-inline-spacing);
    }
  }

  .render-more-sentinel {
    min-height: 1px;
  }
  .show-all-contacts-container {
    border-top: 1px solid var(--color-default-stroke);
  }

  .show-all-contacts-button {
    opacity: 0.8;
    transition: opacity 0.2s ease;
    
    &:hover {
      opacity: 1;
    }
  }

</style>